<template>
    <div class="checkout-confirm">
        <Box :radius="isTablet ? 's' : 'none'" :padding="isTablet ? 'm' : 's'">
            <p class="checkout-confirm-title">
                {{ t({ key: 'shared.trip_summary' }) }}
            </p>

            <PriceBreakdown :combine="true" :hideDiscountCode="false" />
        </Box>

        <div class="checkout-confirm-actions">
            <SMSAddon
                v-if="smsAddon"
                :addon="smsAddon"
                @update:modelValue="handleSMSChange" />

            <FormV2
                v-if="hasDisclaimers && sortedDisclaimers"
                class="checkout-confirm-form"
                id="form_confirm"
                :key="formId"
                :onSubmit="handleSubmit"
                :onInvalidSubmit="handleInvalidSubmit"
                :onValidChange="handleValidChange"
                :initialValues="initialValues"
                :validationSchema="validationSchema">
                <div class="checkout-confirm-form-content">
                    <template v-if="hasCheckboxDisclaimers">
                        <DisclaimerCheckbox
                            v-for="item in sortedDisclaimers.DisclaimersWithCheckbox"
                            :key="item.Id"
                            :item="item" />
                    </template>

                    <AgentMediation class="checkout-confirm-form-content-item" />

                    <template v-if="hasTextDisclaimers">
                        <DisclaimerText
                            v-for="item in sortedDisclaimers.DisclaimersWithText"
                            :key="item.Id"
                            :item="item"
                            class="checkout-confirm-form-content-item" />
                    </template>
                </div>

                <div class="checkout-confirm-form-action">
                    <ButtonImportant
                        type="submit"
                        class="continue"
                        :disabled="isUpdating"
                        :loading="isUpdating">
                        {{ t({ key: 'checkout.make_booking' }) }}
                    </ButtonImportant>
                </div>
            </FormV2>
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, computed, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { isNil, isEmpty } from 'lodash';
    import { Disclaimer, SortedDisclaimers } from '@/api/Checkout/checkout.interfaces';
    import {
        AddonProduct,
        BookingOrigin,
        Cart,
        FlightProduct,
    } from '@/api/Cart/cart.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import {
        logStatsigCheckoutSubmitInvalidState,
        logStatsigCustomerDetailsCompleted,
        logStatsigCheckoutAddRemoveAddon,
    } from '@/tracking/statsig/checkout.functions';
    import { displayErrorModal, handleCustomError } from '@/config/errorHandling';
    import { CheckoutStepState } from '@/store/checkout/types';
    import {
        logAnalyticsCustomerDetailsCompleted,
        logAnalyticsAddRemoveProduct,
    } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { StatsigEvent } from '@/tracking/statsig/types';
    import { EventName } from '@/tracking/analytics/types';
    import { endpoints } from '@/api/endpoints';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { RedirectionType, redirectTo } from '@/helpers/url';
    import { ModalType, Prompted } from '@/store/general/types';
    import { preselectBaggageByDefault } from '@/helpers/baggage';
    import { useRouter } from 'vue-router';
    import { isRequired } from '@ui/components/FormV2/Validations/functions';
    import {
        getRequiredValues,
        scrollToInvalidState,
        sortDisclaimer,
        validRequiredFormValues,
    } from '@/helpers/checkout';
    import { generateUUID } from '@shared/utils/uuid';
    import { submitAllForms } from '@/helpers/formRegistry';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { duplicatePaxList } from '@/helpers/flight';
    import DisclaimerText from './Disclaimers/DisclaimerText.vue';
    import DisclaimerCheckbox from './Disclaimers/DisclaimerCheckbox.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import ButtonImportant from '@ui/components/Button/ButtonImportant.vue';
    import Box from '@ui/components/Box/Box.vue';
    import PriceBreakdown from '@/components/shared/PriceBreakdown/components/PriceBreakdown.vue';
    import AgentMediation from '@/components/shared/AgentMediation/AgentMediation.vue';
    import SMSAddon from './SMSAddon/SMSAddon.vue';

    export default defineComponent({
        name: 'Confirm',
        methods: { t },
        components: {
            AgentMediation,
            Box,
            PriceBreakdown,
            DisclaimerText,
            DisclaimerCheckbox,
            SMSAddon,
            FormV2,
            ButtonImportant,
        },
        props: { sectionId: String },
        setup(props) {
            const store = useStore();
            const router = useRouter();

            const { isTablet } = useScreenSize();

            const formId = ref<string | undefined>(undefined);
            const sortedDisclaimers = ref<SortedDisclaimers | null>(null);
            const initialValues = ref<FormValues | undefined>(undefined);
            const validationSchema = ref<ValidationSchema | undefined>(undefined);

            const cart = computed<Cart>(() => store.state.cart.data);

            const flightProducts = computed<FlightProduct[]>(
                () => store.getters['cart/getFlightProducts']
            );

            const isUpdating = computed<boolean>(
                () => store.state.cart.status === 'UPDATING'
            );

            const disclaimers = computed<Disclaimer[] | null>(
                () => store.getters['content/getCheckoutDisclaimers']
            );

            const states = computed<CheckoutStepState[]>(
                () => store.getters['checkout/getStates']
            );

            const smsAddon = computed<AddonProduct | null>(() => {
                const hasFlightProducts = flightProducts.value.length > 0;
                const addon = store.getters['cart/getConfirmationSMSAddon'];

                return hasFlightProducts && addon ? addon : null;
            });

            const hasSelectedInsuranceAddon = computed<boolean>(() => {
                const addons: AddonProduct[] = store.getters['cart/getSelectedAddons'];

                return addons.some(({ IsInsurance }) => IsInsurance);
            });

            const hasCheckboxDisclaimers = computed<boolean>(() => {
                const items = sortedDisclaimers.value?.DisclaimersWithCheckbox;
                return !isNil(items) && !isEmpty(items);
            });

            const hasTextDisclaimers = computed<boolean>(() => {
                const items = sortedDisclaimers.value?.DisclaimersWithText;
                return !isNil(items) && !isEmpty(items);
            });

            const hasDisclaimers = computed<boolean>(
                () => hasCheckboxDisclaimers.value || hasTextDisclaimers.value
            );

            const showBaggageWarning = computed<boolean>(() => {
                const preSelectNoBaggage = preselectBaggageByDefault();
                const prompted: Prompted = store.getters['general/getPrompted'];

                const hasBaggage = flightProducts.value.some((product) =>
                    product.Pax.some((pax) => pax.AdditionalBaggage.Alternatives?.length > 0)
                );

                const hasSelectedBaggage = flightProducts.value.some((product) =>
                    product.Pax.some((pax) =>
                        pax.AdditionalBaggage.Alternatives?.some(
                            (baggage) => baggage.IsSelected
                        )
                    )
                );

                const shouldShowWarning =
                    preSelectNoBaggage &&
                    hasBaggage &&
                    !hasSelectedBaggage &&
                    !prompted.baggageGlobal &&
                    prompted.baggage.length === 0;

                return shouldShowWarning;
            });

            function sortDisclaimers(items: Disclaimer[] | null): SortedDisclaimers {
                const addons: AddonProduct[] = store.getters['cart/getSelectedAddons'];

                const selectedInsuranceProduct = addons.some(
                    ({ IsInsurance }) => IsInsurance
                );

                const exclude: string[] = [];

                if (!selectedInsuranceProduct) {
                    exclude.push('insuranceConfirmation');
                }

                return sortDisclaimer(items && items.filter((d) => !exclude.includes(d.Id)));
            }

            function setValidationSchema(sorted: SortedDisclaimers): ValidationSchema {
                const schema: ValidationSchema = {
                    // [newsletterDisclaimer.Id]: [],
                };

                sorted.DisclaimersWithCheckbox?.forEach((d) => {
                    schema[d.Id] = [isRequired];
                });

                return schema;
            }

            function setInitialState(items: Disclaimer[] | null) {
                if (!isNil(items)) {
                    const sorted = sortDisclaimers(items);
                    sortedDisclaimers.value = sorted;
                    initialValues.value = setInitialValues(sorted);
                    validationSchema.value = setValidationSchema(sorted);

                    // We do this to reinitialize the form due to intialValues / validationSchema changes
                    formId.value = generateUUID();
                }
            }

            function setInitialValues(sorted: SortedDisclaimers): FormValues {
                const values: FormValues = {};

                sorted.DisclaimersWithCheckbox?.forEach((d) => {
                    values[d.Id] = false;
                });

                return values;
            }

            watch(hasSelectedInsuranceAddon, () => {
                setInitialState(disclaimers.value);
            });

            watch(disclaimers, (newDisclaimers) => setInitialState(newDisclaimers), {
                immediate: true,
            });

            watch(
                () => flightProducts.value.length,
                (flights) => {
                    if (flights === 0) {
                        const selectedAddons: AddonProduct[] =
                            store.getters['cart/getSelectedAddons'];
                        const smsAddon = selectedAddons.find(
                            (addon) => addon.$type === 'FlightConfirmationSms'
                        );

                        if (smsAddon) {
                            store.commit('cart/removeAddon', smsAddon);
                        }
                    }
                }
            );

            function setValid(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            function handleValidChange(valid: boolean) {
                setValid(valid);
            }

            function validateSection(values: any) {
                const requiredValues = getRequiredValues(disclaimers.value);
                const valid = validRequiredFormValues(values, requiredValues);
                setValid(valid);
            }

            async function handleInvalidSubmit() {
                await submitAllForms();
            }

            async function handleSubmit(values: any) {
                await submitAllForms();

                validateSection(values);

                const invalidStates = states.value.filter((x) => !x.valid);

                if (!isEmpty(invalidStates)) {
                    const invalidSectionIds = invalidStates.map((state) => state.id);

                    logStatsigCheckoutSubmitInvalidState(
                        states.value.length,
                        invalidStates.length,
                        invalidSectionIds
                    );
                    scrollToInvalidState();
                    return;
                }

                const noneUniqPaxes = duplicatePaxList(flightProducts.value);
                if (!isEmpty(noneUniqPaxes)) {
                    store.dispatch('general/setModal', {
                        type: ModalType.DuplicatePaxWarning,
                        pax: noneUniqPaxes,
                    });
                    return;
                }

                if (showBaggageWarning.value) {
                    store.dispatch('general/setModal', {
                        type: ModalType.BaggageWarning,
                        onSkip: updateCart,
                    });
                    return;
                }

                updateCart();
            }

            async function updateCart() {
                const totalToPay = store.getters['cart/getTotalToPay'];

                const flowContainsPostCheckout = cart.value.FlowContainsPostCheckout;

                logAnalyticsCustomerDetailsCompleted(totalToPay);
                logStatsigCustomerDetailsCompleted(cart.value, totalToPay);

                const moveToNextStage = !flowContainsPostCheckout;

                try {
                    store.dispatch('cart/setCartUpdating');

                    const response = await endpoints.cart.UpdateCart(
                        cart.value,
                        BookingOrigin.Checkout,
                        moveToNextStage,
                        true
                    );

                    if (isEmpty(response.ErrorMessage)) {
                        store.dispatch('checkout/setClearSessionCart', true);
                    }

                    if (response.ErrorMessage !== null) {
                        handleResetUpdating(response.ErrorMessage, false);
                        handleCustomError(
                            `UpdateCart ErrorMessage: ${response.ErrorMessage}`
                        );
                        trackClarity(ClarityEvent.CHECKOUT_SERVER_VALIDATION_ERROR);
                    } else if (response.NextUrl) {
                        redirectTo(response.NextUrl, RedirectionType.Replace);
                    } else if (flowContainsPostCheckout) {
                        await router
                            .replace({
                                name: 'post-checkout',
                            })
                            .catch(() => {});
                    }
                } catch (error) {
                    handleResetUpdating('checkout.error_on_continue_message');
                    handleCustomError(`UpdateCart ${error}`);
                    trackClarity(ClarityEvent.CHECKOUT_SERVER_VALIDATION_ERROR);
                }
            }

            function handleResetUpdating(message: string, isKey: boolean = true) {
                store.dispatch('cart/resetCartUpdating');
                displayErrorModal(message, isKey);
            }

            function onSelectSMS() {
                if (!smsAddon.value) return;

                store.commit('cart/buyAddon', smsAddon.value);
                logAnalyticsAddRemoveProduct(smsAddon.value, EventName.CHECKOUT_ADD_PRODUCT);
                logStatsigCheckoutAddRemoveAddon(
                    smsAddon.value,
                    StatsigEvent.CHECKOUT_ADD_ADDON
                );
            }

            function onDeselectSMS() {
                if (!smsAddon.value) return;

                store.commit('cart/removeAddon', smsAddon.value);
                logAnalyticsAddRemoveProduct(
                    smsAddon.value,
                    EventName.CHECKOUT_REMOVE_PRODUCT
                );
                logStatsigCheckoutAddRemoveAddon(
                    smsAddon.value,
                    StatsigEvent.CHECKOUT_REMOVE_ADDON
                );
            }

            function handleSMSChange(isChecked: boolean) {
                if (!smsAddon.value) return;

                const selectedAddons = store.getters['cart/getSelectedAddons'];
                const isCurrentlySelected = selectedAddons.some(
                    (addon: AddonProduct) => addon.$type === 'FlightConfirmationSms'
                );

                if (isChecked && !isCurrentlySelected) {
                    onSelectSMS();
                } else if (!isChecked && isCurrentlySelected) {
                    onDeselectSMS();
                }
            }

            return {
                isTablet,
                formId,
                handleSubmit,
                handleInvalidSubmit,
                handleValidChange,
                hasDisclaimers,
                hasCheckboxDisclaimers,
                hasTextDisclaimers,
                sortedDisclaimers,
                isUpdating,
                initialValues,
                validationSchema,
                smsAddon,
                handleSMSChange,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Confirm.scss';
</style>
